/**
 * AdminDocumentAudit — Central ledger of every document generated across the
 * Ecosystem (Events, School, Bookshop, POS...). Every PDF, badge, QR and
 * certificate produced through `services/document_service` is logged here.
 *
 * Route: /admin/documents/audit
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  FileText, CheckCircle2, XCircle, Filter, RefreshCw, FileDown,
  Layers, Building2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const StatCard = ({ label, value, icon: Icon, tone = 'slate', testid }) => {
  const tones = {
    slate: 'from-slate-500/15 to-slate-500/5 text-slate-300 border-slate-400/30',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-300 border-emerald-400/30',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-300 border-rose-400/30',
    violet: 'from-violet-500/15 to-violet-500/5 text-violet-300 border-violet-400/30',
  };
  return (
    <div
      data-testid={testid}
      className={`rounded-xl border bg-gradient-to-br p-4 ${tones[tone]}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="h-4 w-4 opacity-70" />
      </div>
      <div className="mt-2 text-2xl font-semibold">{value ?? '—'}</div>
    </div>
  );
};

const formatBytes = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

export default function AdminDocumentAudit() {
  const [filters, setFilters] = useState({
    document_type: '',
    source_module: '',
    user_id: '',
    tenant_id: '',
    success: '',
    from_date: '',
    to_date: '',
  });
  const [applied, setApplied] = useState(filters);
  const [limit] = useState(50);
  const [skip, setSkip] = useState(0);

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) qs.set(k, v);
    });
    qs.set('limit', String(limit));
    qs.set('skip', String(skip));
    return qs.toString();
  }, [applied, limit, skip]);

  const statsQ = useQuery({
    queryKey: ['doc-audit-stats'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/documents/audit/stats`, { headers: authH() });
      return r.data;
    },
  });

  const listQ = useQuery({
    queryKey: ['doc-audit-list', queryString],
    queryFn: async () => {
      const r = await axios.get(
        `${API}/api/admin/documents/audit?${queryString}`,
        { headers: authH() },
      );
      return r.data;
    },
    keepPreviousData: true,
  });

  const templatesQ = useQuery({
    queryKey: ['doc-templates'],
    queryFn: async () => {
      const r = await axios.get(`${API}/api/admin/documents/templates`, { headers: authH() });
      return r.data;
    },
  });

  const apply = () => {
    setSkip(0);
    setApplied(filters);
  };
  const reset = () => {
    const empty = {
      document_type: '', source_module: '', user_id: '', tenant_id: '',
      success: '', from_date: '', to_date: '',
    };
    setFilters(empty);
    setApplied(empty);
    setSkip(0);
  };

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-slate-100" data-testid="admin-document-audit-page">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="flex items-center gap-3 text-3xl font-bold">
              <FileText className="h-7 w-7 text-violet-400" />
              Document Audit Ledger
            </h1>
            <p className="mt-1 text-sm text-slate-400">
              Every PDF, badge, QR and certificate produced across the ecosystem.
              Sourced from <code className="text-violet-300">document_audit_log</code>.
            </p>
          </div>
          <Button
            data-testid="doc-audit-refresh-btn"
            variant="outline"
            onClick={() => { listQ.refetch(); statsQ.refetch(); }}
            className="border-slate-700 bg-slate-900 hover:bg-slate-800"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard testid="stat-total" label="Total documents" value={statsQ.data?.total} icon={Layers} tone="slate" />
          <StatCard testid="stat-success" label="Successful" value={statsQ.data?.successes} icon={CheckCircle2} tone="emerald" />
          <StatCard testid="stat-failed" label="Failed" value={statsQ.data?.failures} icon={XCircle} tone="rose" />
          <StatCard testid="stat-modules" label="Modules" value={statsQ.data?.by_module?.length} icon={Building2} tone="violet" />
        </div>

        {/* Filters */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4 text-slate-400" /> Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <div>
                <label className="text-xs text-slate-400">Document type</label>
                <Select
                  value={filters.document_type || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, document_type: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-doctype" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any template" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any template</SelectItem>
                    {(templatesQ.data?.templates || []).map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Source module</label>
                <Select
                  value={filters.source_module || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, source_module: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-module" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any module</SelectItem>
                    <SelectItem value="events">events</SelectItem>
                    <SelectItem value="school">school</SelectItem>
                    <SelectItem value="admin">admin</SelectItem>
                    <SelectItem value="donations">donations</SelectItem>
                    <SelectItem value="bookshop">bookshop</SelectItem>
                    <SelectItem value="pos">pos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">Success</label>
                <Select
                  value={filters.success || 'all'}
                  onValueChange={(v) => setFilters((f) => ({ ...f, success: v === 'all' ? '' : v }))}
                >
                  <SelectTrigger data-testid="filter-success" className="mt-1 border-slate-700 bg-slate-950">
                    <SelectValue placeholder="Any" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any</SelectItem>
                    <SelectItem value="true">Success only</SelectItem>
                    <SelectItem value="false">Failed only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-400">User ID</label>
                <Input
                  data-testid="filter-user"
                  value={filters.user_id}
                  onChange={(e) => setFilters((f) => ({ ...f, user_id: e.target.value }))}
                  placeholder="Filter by user id"
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">Tenant ID</label>
                <Input
                  data-testid="filter-tenant"
                  value={filters.tenant_id}
                  onChange={(e) => setFilters((f) => ({ ...f, tenant_id: e.target.value }))}
                  placeholder="Filter by tenant id"
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">From (date)</label>
                <Input
                  data-testid="filter-from"
                  type="date"
                  value={filters.from_date}
                  onChange={(e) => setFilters((f) => ({ ...f, from_date: e.target.value }))}
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400">To (date)</label>
                <Input
                  data-testid="filter-to"
                  type="date"
                  value={filters.to_date}
                  onChange={(e) => setFilters((f) => ({ ...f, to_date: e.target.value }))}
                  className="mt-1 border-slate-700 bg-slate-950"
                />
              </div>
              <div className="flex items-end gap-2">
                <Button data-testid="filter-apply-btn" onClick={apply} className="bg-violet-600 hover:bg-violet-500">
                  Apply
                </Button>
                <Button data-testid="filter-reset-btn" onClick={reset} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800">
                  Reset
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-slate-800 bg-slate-900/60">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-base">
              Entries {total ? <span className="ml-2 text-xs font-normal text-slate-400">({total} total)</span> : null}
            </CardTitle>
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <FileDown className="h-4 w-4" /> Latest first
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-400">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Document</th>
                  <th className="py-2 pr-4">Module</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Subject</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {listQ.isLoading && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500">Loading…</td>
                  </tr>
                )}
                {!listQ.isLoading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-slate-500" data-testid="audit-empty">
                      No documents match these filters.
                    </td>
                  </tr>
                )}
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/60 hover:bg-slate-900/40" data-testid="audit-row">
                    <td className="py-2 pr-4 text-slate-300">{formatDate(row.created_at)}</td>
                    <td className="py-2 pr-4">
                      <div className="font-medium">{row.document_type || row.template}</div>
                      <div className="text-xs text-slate-500">{row.filename || '—'}</div>
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant="outline" className="border-slate-700 text-slate-300">
                        {row.source_module || 'unknown'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 text-slate-300">
                      <div>{row.user_email || '—'}</div>
                      <div className="text-xs text-slate-500">{row.user_id || ''}</div>
                    </td>
                    <td className="py-2 pr-4 text-slate-400">{row.subject_id || '—'}</td>
                    <td className="py-2 pr-4 text-slate-400">{formatBytes(row.size_bytes)}</td>
                    <td className="py-2 pr-4">
                      {row.success ? (
                        <span className="inline-flex items-center gap-1 text-emerald-400">
                          <CheckCircle2 className="h-4 w-4" /> ok
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-rose-400" title={row.error || ''}>
                          <XCircle className="h-4 w-4" /> failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Pagination */}
        <div className="flex items-center justify-between text-sm text-slate-400">
          <div>
            Showing {items.length ? skip + 1 : 0}–{skip + items.length} of {total}
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="audit-prev-btn"
              variant="outline"
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              disabled={skip === 0}
              onClick={() => setSkip((s) => Math.max(0, s - limit))}
            >
              Previous
            </Button>
            <Button
              data-testid="audit-next-btn"
              variant="outline"
              className="border-slate-700 bg-slate-900 hover:bg-slate-800"
              disabled={skip + items.length >= total}
              onClick={() => setSkip((s) => s + limit)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
